var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete, Checkbox, ClickAwayListener, TextField, Typography } from '@mui/material';
import { differenceBy, groupBy, intersectionWith, union, without } from 'lodash';
import EditableInputWrapperV2 from '../../../../components/EditableInputWrapperV2';
import LabelWrapper from '../../LabelWrapper';
import { isOptionTypeArray } from '../DropDown';
import CollapseOptionsGroup from './CollapseOptionsGroup';
import ListBoxComponent from './ListBoxComponent';
import PaperComponent from './PaperComponent';
import VirtualizedListBox from './VirtualizedListBox';
var MultiSelectDropDown = function (_a) {
    var label = _a.label, optionsProp = _a.options, renderInputProp = _a.renderInput, error = _a.error, helperText = _a.helperText, placeholder = _a.placeholder, _b = _a.enableGrouping, enableGrouping = _b === void 0 ? false : _b, getOptionLabel = _a.getOptionLabel, defaultValue = _a.defaultValue, _c = _a.enableVirtualization, enableVirtualization = _c === void 0 ? false : _c, onChange = _a.onChange, required = _a.required, valueProp = _a.value, editable = _a.editable, displayValue = _a.displayValue, hideSearchOption = _a.hideSearchOption, rest = __rest(_a, ["label", "options", "renderInput", "error", "helperText", "placeholder", "enableGrouping", "getOptionLabel", "defaultValue", "enableVirtualization", "onChange", "required", "value", "editable", "displayValue", "hideSearchOption"]);
    var _d = __read(useState(isOptionTypeArray(defaultValue) ? defaultValue : []), 2), values = _d[0], setValues = _d[1];
    var _e = __read(useState([]), 2), options = _e[0], setOptions = _e[1];
    var _f = __read(useState([]), 2), groupedOptions = _f[0], setGroupedOptions = _f[1];
    var _g = __read(useState(''), 2), searchQuery = _g[0], setSearchQuery = _g[1];
    var _h = __read(useState(false), 2), open = _h[0], setOpen = _h[1];
    var _j = __read(useState('options'), 2), dropdownViewMode = _j[0], setDropdownViewMode = _j[1];
    var groupedByOptionsArray = React.useMemo(function () { return Object.values(groupedOptions).flat(); }, [groupedOptions]);
    var groupedByOptions = groupBy(groupedByOptionsArray, 'groupBy');
    var autoCompleteRef = useRef(null);
    var inputRef = useRef(null);
    useEffect(function () {
        var _a;
        if (isOptionTypeArray(valueProp)) {
            var presents = intersectionWith(enableGrouping ? groupedByOptionsArray : optionsProp, valueProp, function (op1, op2) { return op1.value === op2.value; });
            setValues(presents);
        }
        else {
            setValues((_a = valueProp) !== null && _a !== void 0 ? _a : []);
        }
    }, [groupedByOptionsArray, enableGrouping, valueProp, optionsProp]);
    useEffect(function () {
        if (enableGrouping && Array.isArray(optionsProp)) {
            var groupedOptions_1 = [];
            optionsProp.forEach(function (option) {
                var _a;
                if (Array.isArray(option === null || option === void 0 ? void 0 : option.childrens)) {
                    ((_a = option === null || option === void 0 ? void 0 : option.childrens) !== null && _a !== void 0 ? _a : []).forEach(function (childOption) {
                        return groupedOptions_1.push(__assign(__assign({}, childOption), { groupBy: option.label }));
                    });
                }
            });
            setGroupedOptions(groupedOptions_1);
            setOptions(groupedOptions_1);
        }
        else {
            setOptions(Array.isArray(optionsProp) ? __spreadArray([], __read(optionsProp), false) : []);
        }
    }, [optionsProp, enableGrouping]);
    var getTextWidth = function (text) {
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');
        if (!context)
            return 0;
        context.font = getComputedStyle(document.body).font;
        return context.measureText(text).width;
    };
    var renderTags = function (values) {
        var _a;
        if (!isOptionTypeArray(values))
            return React.createElement(React.Fragment, null);
        if (values.length === 1)
            return (_a = values[0]) === null || _a === void 0 ? void 0 : _a.label;
        if (autoCompleteRef.current && getComputedStyle) {
            var autoCompleteStyles = getComputedStyle(autoCompleteRef.current);
            var valuesToShowCount = 0;
            var visibleValuesWidth = 0;
            for (var i = 0; i < values.length; i++) {
                visibleValuesWidth += getTextWidth(values[i].label);
                if (visibleValuesWidth >
                    autoCompleteStyles.getPropertyValue('width').split('px')[0] / 2) {
                    break;
                }
                else {
                    valuesToShowCount += 1;
                }
            }
            var remainingValuesCount = values.length - (valuesToShowCount || 1);
            var startLabel = void 0;
            if (valuesToShowCount === 0 && values.length > 0) {
                startLabel = values[0].label.substring(0, 10) + '...';
            }
            else {
                startLabel = values
                    .slice(0, valuesToShowCount)
                    .map(function (value) { return value.label; })
                    .join(' , ');
            }
            return startLabel + (remainingValuesCount > 0 && values.length > 1 ? " +".concat(remainingValuesCount, " more") : '');
        }
        return React.createElement(React.Fragment, null);
    };
    var handleToggleOption = function (selectedOptions) {
        setValues(selectedOptions);
    };
    var handleClearAllOptions = function () {
        onChange === null || onChange === void 0 ? void 0 : onChange(null, [], 'clear');
        setValues([]);
    };
    var handleClearOptions = function () {
        setValues(function (oldVale) {
            return differenceBy(oldVale, options, 'value');
        });
        onChange === null || onChange === void 0 ? void 0 : onChange(null, differenceBy(values, options, 'value'), 'clear');
    };
    var handleSelectAll = function (event) {
        // remove disabled options while doing this
        handleToggleOption(Array.from(new Set(__spreadArray(__spreadArray([], __read(values), false), __read(options.filter(function (item) { return !(item === null || item === void 0 ? void 0 : item.isDisabled); })), false))));
        onChange === null || onChange === void 0 ? void 0 : onChange(event, Array.from(new Set(__spreadArray(__spreadArray([], __read(values), false), __read(options.filter(function (item) { return !(item === null || item === void 0 ? void 0 : item.isDisabled); })), false))), 'selectOption');
    };
    var handleViewAll = function () {
        if (dropdownViewMode === 'options') {
            setDropdownViewMode('values');
            setOptions(values);
            return 'values';
        }
        else {
            setDropdownViewMode('options');
            setOptions(enableGrouping ? groupedByOptionsArray : optionsProp);
            return 'options';
        }
    };
    var handleChange = function (event, selectedOptions, reason, details) {
        if (!((selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.length) === 0) && !isOptionTypeArray(selectedOptions))
            return;
        if (reason === 'selectOption' || reason === 'removeOption') {
            handleToggleOption(selectedOptions);
        }
        else if (reason === 'clear') {
            handleClearAllOptions();
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(event, selectedOptions, reason, details);
    };
    var allSubGroupSelected = function (groupKey) {
        var _a, _b, _c;
        var selectedGroup = groupBy(values, 'groupBy');
        return {
            all: ((_a = groupedByOptions[groupKey]) === null || _a === void 0 ? void 0 : _a.length) === ((_b = selectedGroup[groupKey]) === null || _b === void 0 ? void 0 : _b.length),
            indeterminate: ((_c = selectedGroup[groupKey]) === null || _c === void 0 ? void 0 : _c.length) > 0 && selectedGroup[groupKey].length < groupedByOptions[groupKey].length
        };
    };
    var handleSubSelectAll = function (groupedBy) {
        if (allSubGroupSelected(groupedBy).all) {
            setValues(function (oldVale) {
                return without.apply(void 0, __spreadArray([oldVale], __read(groupedByOptions[groupedBy]), false));
            });
        }
        else {
            setValues(function (oldVale) {
                return union(oldVale, groupedByOptions[groupedBy]);
            });
        }
    };
    var handleSearch = function (query) {
        setSearchQuery(query);
        if (query === '') {
            setOptions(enableGrouping ? groupedByOptionsArray : optionsProp);
            return;
        }
        if (enableGrouping) {
            var newOptions = groupedByOptionsArray.filter(function (option) {
                return option.label.toLowerCase().trim().includes(query.toLowerCase());
            });
            setOptions(newOptions);
        }
        else {
            var newOptions = optionsProp.filter(function (option) {
                return option.label.toLowerCase().trim().includes(query.toLowerCase());
            });
            setOptions(newOptions);
        }
    };
    var handleClickAway = function () {
        setOpen(false);
    };
    return (React.createElement(LabelWrapper, { label: label, disabled: rest === null || rest === void 0 ? void 0 : rest.disabled, required: required },
        React.createElement(EditableInputWrapperV2, { editable: editable, value: displayValue === null || displayValue === void 0 ? void 0 : displayValue(values) },
            React.createElement(ClickAwayListener, { onClickAway: handleClickAway },
                React.createElement("div", { className: "lfc-relative" },
                    React.createElement(Autocomplete, __assign({ popupIcon: React.createElement(ExpandMoreIcon, null), onOpen: function () {
                            setOpen(true);
                        }, open: open, disableClearable: true, disableCloseOnSelect: true, ref: autoCompleteRef, onClose: function (event, reason) {
                            if (reason === 'escape' || reason === 'toggleInput') {
                                setOpen(false);
                            }
                        }, multiple: true, renderGroup: function (props) { return (React.createElement(CollapseOptionsGroup, __assign({ allSubGroupSelected: allSubGroupSelected, handleSubSelectAll: handleSubSelectAll }, props))); }, PaperComponent: PaperComponent, ListboxComponent: enableVirtualization ? VirtualizedListBox : ListBoxComponent, isOptionEqualToValue: function (option, value) { var _a; return (option === null || option === void 0 ? void 0 : option.value) === ((_a = value === null || value === void 0 ? void 0 : value.value) !== null && _a !== void 0 ? _a : value); }, renderTags: renderTags, renderInput: function (params) {
                            var _a, _b, _c;
                            inputRef.current = (_b = (_a = params.inputProps) === null || _a === void 0 ? void 0 : _a.ref) === null || _b === void 0 ? void 0 : _b.current;
                            return ((_c = renderInputProp === null || renderInputProp === void 0 ? void 0 : renderInputProp(params)) !== null && _c !== void 0 ? _c : (React.createElement(TextField, __assign({}, params, (typeof params.InputProps.startAdornment === 'string'
                                ? {
                                    InputProps: __assign(__assign({}, params.InputProps), { startAdornment: null }),
                                    inputProps: __assign(__assign({}, params.inputProps), { value: params.InputProps.startAdornment })
                                }
                                : {}), { sx: {
                                    input: {
                                        cursor: 'pointer',
                                        caretColor: 'white'
                                    }
                                }, error: error, helperText: helperText, placeholder: placeholder, label: undefined, onKeyPress: function (e) {
                                    e.preventDefault();
                                } }))));
                        }, renderOption: function (props, option) {
                            var _a, _b;
                            return (React.createElement("li", __assign({}, props, { className: "".concat((_a = props.className) !== null && _a !== void 0 ? _a : ''), key: String(option.value) }),
                                React.createElement(Checkbox, { checked: Boolean(props['aria-selected']) }),
                                React.createElement(Typography, { className: "lfc-break-all" },
                                    " ", (_b = getOptionLabel === null || getOptionLabel === void 0 ? void 0 : getOptionLabel(option)) !== null && _b !== void 0 ? _b : option.label)));
                        } }, (enableGrouping ? { groupBy: function (option) { var _a; return (_a = option === null || option === void 0 ? void 0 : option.groupBy) !== null && _a !== void 0 ? _a : ''; } } : {}), rest, { componentsProps: __assign(__assign({}, rest === null || rest === void 0 ? void 0 : rest.componentsProps), { paper: {
                                initialSeachQuery: searchQuery,
                                onSearch: handleSearch,
                                selectAll: handleSelectAll,
                                selectedCount: values.length,
                                clearSelection: handleClearOptions,
                                toggleDropDownView: handleViewAll,
                                hideSearchOption: hideSearchOption
                            } }), options: options !== null && options !== void 0 ? options : [], value: values, onChange: handleChange })))))));
};
export default MultiSelectDropDown;
//# sourceMappingURL=index.js.map